import { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react-lite";
import { DynamicForm } from "@blings/dynamic-form";
import "./DataSetter.scss";
import { useMst } from "../../stores/main";
import BlingsTheme from "../../config/antdTheme";
import { isEqual } from "lodash";
import {
  cloneDynamicFormData,
  detectRenamedKey,
  transformDataWithRenamedKey,
} from "../../helpers/dynamicFormKeyRename";
import type { DynamicFormData } from "../../helpers/dynamicFormKeyRename";
import { getDynamicFormInstanceKey } from "../../helpers/dynamicFormSchemaKey";

export const DataSetter = observer(() => {
  const {
    platformStore,
    dynamicDataStore: {
      setCurrentPerVideoDocumentFromForm,
      perVideoCurrentSchema,
      perVideoCurrentData,
      updateModsWithRenamedKeys,
      perVideoSchemaRenderKey,
    },
  } = useMst();

  const submit = useRef<() => void>(() => {
    return;
  });

  const previousFormDataRef = useRef<DynamicFormData>(
    cloneDynamicFormData(perVideoCurrentData as DynamicFormData)
  );

  const isFirstChange = useRef(true);

  useEffect(() => {
    previousFormDataRef.current = cloneDynamicFormData(
      perVideoCurrentData as DynamicFormData
    );
  }, [perVideoCurrentData]);

  const dynamicFormInstanceKey = useMemo(
    () =>
      getDynamicFormInstanceKey(perVideoCurrentSchema, perVideoSchemaRenderKey),
    [perVideoCurrentSchema, perVideoSchemaRenderKey]
  );

  if (!platformStore.projectWorkspaceVersion)
    return <div className={"editor-wrapper"}></div>;

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <div className={"editor-wrapper"}>
        <DynamicForm
          schemaType="data"
          themeConfig={BlingsTheme}
          key={dynamicFormInstanceKey}
          schema={JSON.parse(perVideoCurrentSchema || "{}")}
          formData={JSON.parse(JSON.stringify(perVideoCurrentData))}
          saveData={(data, schema) => {
            const document = setCurrentPerVideoDocumentFromForm(schema, data);
            platformStore.savePerVideoSchema(document.schema);
          }}
          uiMode="tree"
          insertAt="start"
          submit={submit}
          editable={true}
          uploadAssetToProject={platformStore.uploadAssetToProject}
          getAssetsUploadStatus={platformStore.getDynamicFormAssetsUploadStatus}
          onChange={(formData: DynamicFormData, schema) => {
            // DynamicForm emits an initial no-op change while MobX data settles.
            if (isFirstChange.current) {
              isFirstChange.current = false;
              if (isEqual(formData, previousFormDataRef.current)) {
                previousFormDataRef.current = cloneDynamicFormData(formData);
                return;
              }
            }

            const renamed = detectRenamedKey(
              previousFormDataRef.current,
              formData
            );
            if (renamed) {
              updateModsWithRenamedKeys(renamed.oldKey, renamed.newKey);
              const transformedData = transformDataWithRenamedKey(
                previousFormDataRef.current,
                renamed.oldKey,
                renamed.newKey
              );
              previousFormDataRef.current = cloneDynamicFormData(
                transformedData
              );
              setCurrentPerVideoDocumentFromForm(schema, transformedData);

              return;
            }

            previousFormDataRef.current = cloneDynamicFormData(formData);

            setCurrentPerVideoDocumentFromForm(schema, formData);
          }}
          readable={false}
          fieldDescription={{
            key:
              "A name for this data field. Each video might get different value at run-time",
            value:
              "This is an example value. Real per-video data can differ for each user; " +
              "edit it here to update the player and test different values.",
          }}
        />
      </div>
    </div>
  );
});
